import { foldText, foldWithIndexMap, originalRange } from '@/shared/utils/searchFold';

import { wholeArabicWords, type TextRange } from './match';

/**
 * Quick Find's local groups (Package G, G-1 and G-2): the places the shell's
 * own navigation offers this seat ("Go to"), the guides and coaches it is
 * offered ("Help"), and the navigation-only "Start" commands its own pages
 * offer it (QF-14). All are matched HERE, on labels the shell already holds —
 * no request, nothing stored — so they are kept out of the record families
 * (`PORTAL_FAMILIES`), which only the server answers.
 */

/** One place a shell's navigation offers this seat: where it goes and what it is called there. */
export interface QuickFindPlace {
  to: string;
  /** The label the navigation shows, in the language on screen. */
  label: string;
  /**
   * QF-09 — the same entry's label in the other language, so a person typing
   * "approvals" on an Arabic screen still finds «الاعتمادات». Matched, never
   * shown in place of the label; it widens nothing (same list, same gates).
   */
  altLabel?: string;
  /** Where it sits in the navigation ("Procurement"), when that helps tell two apart. */
  context?: string;
  /**
   * QF-14 — `command`: a "Start" entry, the page a create action BEGINS on
   * (Start enquiry, Create organisation). Navigation only: choosing it opens
   * the page, which re-checks the permission and asks before anything is
   * saved. Offered only where the page's own primary button is.
   */
  kind?: 'place' | 'command';
}

/** At most this many of each local group, so the records below stay in view. */
export const LOCAL_LIMIT = 5;

/** The shortest term a label is matched for — the record search's own minimum. */
const MIN_LOCAL = 2;

// What separates the words of a label: spaces and the punctuation labels use.
const SEPARATORS = /[\s\-–—&,،·/()]+/u;
const WORD = /[^\s\-–—&,،·/()]+/gu;
/** The Arabic definite article, which a person may or may not type. */
const ARTICLE = 'ال';

/**
 * A label or a term folded as the record search folds a term (SearchTerm.php).
 * The shared fold (`shared/utils/searchFold`, PUX UI-2), under the name Quick
 * Find has always used for it.
 */
export const foldLabel = foldText;

function words(folded: string): string[] {
  return folded.split(SEPARATORS).filter((word) => word !== '');
}

/** The words of a folded label, where each starts — and, for «ال…», also without its article. */
function startsOf(folded: string): { at: number; word: string }[] {
  return [...folded.matchAll(WORD)].flatMap((match) => {
    const word = match[0];
    const at = match.index;

    return word.startsWith(ARTICLE) && word.length > ARTICLE.length + 1
      ? [
          { at, word },
          { at: at + ARTICLE.length, word: word.slice(ARTICLE.length) },
        ]
      : [{ at, word }];
  });
}

/**
 * Whether every word of the term begins a word of the label — an Arabic word
 * also without its article («استلام» finds «الاستلام»). Two characters at
 * least, as the record search asks.
 */
export function matchesLabel(label: string, term: string): boolean {
  const wanted = words(foldLabel(term));

  if (wanted.join('').length < MIN_LOCAL) return false;

  const have = startsOf(foldLabel(label)).map(({ word }) => word);

  return wanted.every((part) => have.some((word) => word.startsWith(part)));
}

/**
 * QF-01 — where in the label, as written, each word of the term begins a word
 * (the label's matching rule above): the ranges a highlight marks. An Arabic
 * range is widened to its whole word, so a word's letters are never split
 * across elements (cursive joining). Empty when the label does not match.
 */
export function labelRanges(label: string, term: string): TextRange[] {
  if (!matchesLabel(label, term)) return [];

  const map = foldWithIndexMap(label);
  const starts = startsOf(map.folded);
  const ranges = words(foldLabel(term)).flatMap((part): TextRange[] => {
    const hit = starts.find(({ word }) => word.startsWith(part));

    return hit === undefined ? [] : [originalRange(map, hit.at, hit.at + part.length)];
  });

  return wholeArabicWords(label, ranges);
}

/** A place that matched, and on which of its labels. */
export interface PlaceMatch {
  place: QuickFindPlace;
  /** `alt`: only the other language's label matched (shown as a hint beside it). */
  via: 'label' | 'alt';
}

/** The places whose label (or other-language label) matches, in the navigation's own order. */
export function matchPlaces(places: readonly QuickFindPlace[], term: string): PlaceMatch[] {
  const seen = new Set<string>();

  return places
    .flatMap((place): PlaceMatch[] => {
      if (seen.has(place.to)) return [];

      const via = matchesLabel(place.label, term)
        ? 'label'
        : place.altLabel !== undefined && matchesLabel(place.altLabel, term)
          ? 'alt'
          : null;

      if (via === null) return [];

      seen.add(place.to);

      return [{ place, via }];
    })
    .slice(0, LOCAL_LIMIT);
}
